import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useAuthStore } from '@/store/auth.store';
import { userService } from '@/services/user.service';

interface Props {
  children: ReactNode;
}

export function AuthProvider({ children }: Props) {
  const { setFirebaseUser, setAppUser, setLoading, setInitialized, reset } = useAuthStore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      if (user) {
        // Force a token refresh so Firebase Storage/Firestore rules always see
        // up-to-date custom claims (role, tenantId, companyId).
        try { await user.getIdToken(true); } catch { /* network issue — proceed with cached token */ }
        setFirebaseUser(user);
        try {
          const appUser = await userService.getById(user.uid);
          setAppUser(appUser);
        } catch (err) {
          console.error('[AuthProvider] Failed to load user profile:', err);
          setAppUser(null);
        }
      } else {
        reset();
      }
      setLoading(false);
      setInitialized(true);
    });

    return () => unsubscribe();
  }, [setFirebaseUser, setAppUser, setLoading, setInitialized, reset]);

  return <>{children}</>;
}
